import { ANALYTICS_EVENTS } from './events';

const SENSITIVE_KEY = /^(email|display_name|full_name|first_name|last_name|username|phone|address|date_of_birth|dob|.*token.*|password|answer|question|question_text|selected_answer|correct_answer|search_query|message)$/i;
const MAX_STRING_LENGTH = 100;

// Query parameters that must never reach an analytics provider. Reset and
// verification links carry single-use secrets in `token`; OAuth flows use the
// others. Anything matching is dropped before a URL is sent anywhere.
const SENSITIVE_QUERY_PARAM = /^(token|code|access_token|refresh_token|id_token|authorization|state|restoreToken|restore_token|.*token.*|.*secret.*|password|api_key|apikey)$/i;
// Routes whose query string is never analytics-safe as a whole.
const SENSITIVE_PATHS = /^\/(verify-email|reset-password|restore-account)(\/|$)/;

/**
 * Reduce a URL or path to what analytics may see: the pathname plus only
 * non-sensitive query params (and no query at all on token-bearing routes).
 * Never throws; falls back to '/' for garbage input.
 */
export function sanitizeAnalyticsPath(input) {
    try {
        const url = new URL(String(input ?? '/'), 'https://aluvo.app');
        if (SENSITIVE_PATHS.test(url.pathname)) return url.pathname;
        const params = new URLSearchParams();
        url.searchParams.forEach((value, key) => {
            if (!SENSITIVE_QUERY_PARAM.test(key)) params.append(key, value.slice(0, MAX_STRING_LENGTH));
        });
        const query = params.toString();
        return `${url.pathname}${query ? `?${query}` : ''}`;
    } catch {
        return '/';
    }
}

/** Full URL form of sanitizeAnalyticsPath, keeping only the origin + sanitized path. */
export function sanitizeAnalyticsUrl(input) {
    try {
        const url = new URL(String(input ?? ''), typeof window !== 'undefined' ? window.location.origin : 'https://aluvo.app');
        return `${url.origin}${sanitizeAnalyticsPath(url.href)}`;
    } catch {
        return sanitizeAnalyticsPath(input);
    }
}
let lastPageKey = null;
const criticalEvents = new Set();

const enabled = () => process.env.REACT_APP_ANALYTICS_ENABLED === 'true' && process.env.NODE_ENV !== 'test';
export const isExcludedRoute = (path = '') => /^\/admin(?:\/|$)/.test(path) || /^\/internal(?:\/|$)/.test(path);

export function sanitizeParameters(parameters = {}) {
    return Object.fromEntries(Object.entries(parameters).flatMap(([key, value]) => {
        if (SENSITIVE_KEY.test(key) || value == null || typeof value === 'function' || typeof value === 'object') return [];
        if (!['string', 'number', 'boolean'].includes(typeof value)) return [];
        return [[key, typeof value === 'string' ? value.slice(0, MAX_STRING_LENGTH) : value]];
    }));
}

function injectScript(id, src) {
    if (document.getElementById(id)) return;
    const script = document.createElement('script');
    script.id = id; script.async = true; script.src = src;
    document.head.appendChild(script);
}

export function initializeAnalytics(path) {
    path = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
    if (typeof window === 'undefined' || typeof document === 'undefined' || !enabled() || isExcludedRoute(path)) return false;
    const gaId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    const clarityId = process.env.REACT_APP_CLARITY_PROJECT_ID;
    if (!gaId && !clarityId) return false;
    if (gaId && !window.gtag) {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag() { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        // page_location is pinned to the sanitized URL so GA's automatic
        // collection never picks up a token from the address bar.
        window.gtag('config', gaId, { send_page_view: false, page_location: sanitizeAnalyticsUrl(window.location.href) });
        injectScript('aluvo-ga4', `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`);
    }
    if (clarityId && !window.clarity) {
        window.clarity = function clarity() { (window.clarity.q = window.clarity.q || []).push(arguments); };
        injectScript('aluvo-clarity', `https://www.clarity.ms/tag/${encodeURIComponent(clarityId)}`);
    }
    return true;
}

export function trackPageView(path, title) {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    title = title || document.title;
    if (isExcludedRoute(path)) {
        window.clarity?.('stop');
        return;
    }
    if (!initializeAnalytics(path)) return;
    const safePath = sanitizeAnalyticsPath(path);
    const key = `${safePath}|${title}`;
    if (lastPageKey === key) return;
    lastPageKey = key;
    window.gtag?.('event', 'page_view', {
        page_path: safePath,
        page_title: title,
        page_location: sanitizeAnalyticsUrl(window.location.href),
    });
}

export function trackEvent(name, parameters = {}, options = {}) {
    if (!Object.values(ANALYTICS_EVENTS).includes(name) || !initializeAnalytics()) return;
    if (options.onceKey) {
        const key = `${name}:${options.onceKey}`;
        if (criticalEvents.has(key)) return;
        criticalEvents.add(key);
    }
    // Every event carries an explicit, sanitized page_location so GA never
    // falls back to document.location for it.
    const safe = { ...sanitizeParameters(parameters), page_location: sanitizeAnalyticsUrl(window.location.href) };
    if (process.env.REACT_APP_ANALYTICS_DEBUG === 'true' && process.env.NODE_ENV !== 'production') console.info('[analytics]', name, safe);
    window.gtag?.('event', name, safe);
    const clarityEvents = { challenge_started: 'started_challenge', challenge_completed: 'completed_challenge', challenge_abandoned: 'abandoned_challenge', coin_earned: 'earned_coin', legendary_earned: 'earned_legendary', collection_completed: 'completed_collection' };
    if (clarityEvents[name]) window.clarity?.('event', clarityEvents[name]);
}

export function setAnalyticsUser(userId) {
    if (!userId || !initializeAnalytics()) return;
    const opaque = String(userId).slice(0, 64);
    window.gtag?.('set', { user_id: opaque });
    window.clarity?.('identify', opaque);
    window.clarity?.('set', 'authentication', 'authenticated');
}

export function clearAnalyticsUser() {
    if (typeof window === 'undefined') return;
    window.gtag?.('set', { user_id: null });
    window.clarity?.('set', 'authentication', 'anonymous');
}

export function resetAnalyticsForTests() { lastPageKey = null; criticalEvents.clear(); }
export { ANALYTICS_EVENTS };
